import { useIsOffline } from 'components/common/withIsOffline';
import { useMemo } from 'react';
import { useCurrentUserInProject } from 'store/user';
import { CompanyPermissionManager } from 'shared/domain/permissions/company/companyPermissionManager';
import { ContractPermissionManager } from 'shared/domain/permissions/contract/contractPermissionManager';
import { InspectionPermissionManager } from 'shared/domain/permissions/inspection/inspectionPermissionManager';
import { InspectionTemplatePermissionManager } from 'shared/domain/permissions/inspectionTemplate/inspectionTemplatePermissionManager';
import { IssuePermissionManager } from 'shared/domain/permissions/issue/issuePermissionManager';
import { LevelPermissionManager } from 'shared/domain/permissions/level/level';
import { OrganizationPermissionManager } from 'shared/domain/permissions/organization/organizationPermissionManager';
import { ProjectPermissionManager } from 'shared/domain/permissions/project/projectPermissionManager';
import { SitePermissionManager } from 'shared/domain/permissions/site/sitePermissionManager';
import {
  UserPermissionManager
} from 'shared/domain/permissions/user/userPermissionManager';
import { UserRole } from 'shared/types/userRole';

export const usePermissions = () => {
  const isOffline = useIsOffline();
  const { role: userRole, _id: authorId } = useCurrentUserInProject();
  const issuePermissionManager = useMemo(
    () => new IssuePermissionManager(userRole),
    [userRole]
  );

  const companyPermissionManager = useMemo(
    () => new CompanyPermissionManager(userRole),
    [userRole]
  );

  const contractPermissionManager = useMemo(
    () => new ContractPermissionManager(userRole),
    [userRole]
  );

  const inspectionPermissionManager = useMemo(
    () => new InspectionPermissionManager(userRole),
    [userRole]
  );

  const inspectionTemplatePermissionManager = useMemo(
    () => new InspectionTemplatePermissionManager(userRole),
    [userRole]
  );

  const levelPermissionManager = useMemo(
    () => new LevelPermissionManager(userRole),
    [userRole]
  );

  const sitePermissionManager = useMemo(
    () => new SitePermissionManager(userRole),
    [userRole]
  );

  const userPermissionManager = useMemo(
    () => new UserPermissionManager(userRole),
    [userRole]
  );

  const projectPermissionManager = useMemo(
    () => new ProjectPermissionManager(userRole),
    [userRole]
  );

  const organizationPermissionManager = useMemo(
    () => new OrganizationPermissionManager(userRole),
    [userRole]
  );

  const permissions = useMemo(
    () => ({
      canCreateIssue: () => issuePermissionManager.canCreate(isOffline),
      // canEditIssue: () => issuePermissionManager.canEdit(isOffline),
      // canDeleteIssue: () => issuePermissionManager.canDelete(isOffline),

      canCreateCompany: () =>
        companyPermissionManager.canCreate(isOffline),
      canEditCompany: () => companyPermissionManager.canEdit(isOffline),
      canDeleteCompany: () =>
        companyPermissionManager.canDelete(isOffline),

      canCreateContract: () =>
        contractPermissionManager.canCreate(isOffline),
      canEditContract: () => contractPermissionManager.canEdit(isOffline),
      canDeleteContract: () =>
        contractPermissionManager.canDelete(isOffline),

      canCreateProject: () =>
        projectPermissionManager.canCreate(isOffline),
      canEditProject: () => projectPermissionManager.canEdit(isOffline),

      canCreateInspection: (
        isInspectionTemplatePublished: boolean,
        isInspectionTemplateArchived?: boolean
      ) =>
        inspectionPermissionManager.canCreate(isOffline, {
          isInspectionTemplatePublished,
          isInspectionTemplateArchived,
        }),
      canEditInspection: (isCompleted: boolean, deleted?: boolean) =>
        inspectionPermissionManager.canEdit(isOffline, { isCompleted, deleted }),
      canDeleteInspection: (deleted?: boolean) =>
        inspectionPermissionManager.canDelete(isOffline, { deleted }),

      canCreateInspectionTemplate: () =>
        inspectionTemplatePermissionManager.canCreate(isOffline),
      canEditInspectionTemplate: (
        isInspectionTemplatePublished: boolean,
        deleted?: boolean
      ) =>
        inspectionTemplatePermissionManager.canEdit(isOffline, {
          isInspectionTemplatePublished,
          deleted,
        }),
      canDeleteInspectionTemplate: () =>
        inspectionTemplatePermissionManager.canDelete(isOffline),

      canCreateSite: () => sitePermissionManager.canCreate(isOffline),
      canEditSite: () => sitePermissionManager.canEdit(isOffline),
      canDeleteSite: () => sitePermissionManager.canDelete(isOffline),

      canCreateLevel: () => levelPermissionManager.canCreate(isOffline),
      canEditLevel: () => levelPermissionManager.canEdit(isOffline),
      canDeleteLevel: () => levelPermissionManager.canDelete(isOffline),

      canCreateUser: () => userPermissionManager.canCreate(isOffline),
      canEditUser: (
        modifiedUserId: string,
        modifiedUserRole: UserRole
      ) =>
        userPermissionManager.canEdit(isOffline, {
          authorId,
          authorRole: userRole,
          modifiedUserId,
          modifiedUserRole,
        }),
      canDeleteUser: (
        modifiedUserId: string,
        modifiedUserRole: UserRole
      ) =>
        userPermissionManager.canDelete(isOffline, {
          authorId,
          authorRole: userRole,
          modifiedUserId,
          modifiedUserRole,
        }),
      canCreateOrganization: () =>
        organizationPermissionManager.canCreate(isOffline),
      canEditOrganization: () =>
        organizationPermissionManager.canEdit(isOffline),
    }),
    [
      isOffline,
      userRole,
      companyPermissionManager,
      contractPermissionManager,
      inspectionPermissionManager,
      inspectionTemplatePermissionManager,
      levelPermissionManager,
      sitePermissionManager,
      userPermissionManager,
      projectPermissionManager,
      organizationPermissionManager,
    ]
  );

  return permissions;
};
